"use client";

import { useEffect, useRef, useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface Service {
  icon: string;
  title: string;
  description: string;
}

const services: Service[] = [
  {
    icon: "HeartIcon",
    title: "General Gynecological Care",
    description: "Comprehensive women's health services including routine check-ups, preventive care, and treatment of gynecological conditions."
  },
  {
    icon: "SparklesIcon",
    title: "Fertility Care",
    description: "Expert fertility assessment, counseling, and treatment options to help you achieve your family planning goals."
  },
  {
    icon: "BeakerIcon",
    title: "PCOS Clinic",
    description: "Specialized diagnosis and management of Polycystic Ovary Syndrome with personalized treatment plans."
  },
  {
    icon: "ShieldCheckIcon",
    title: "Endometriosis Clinic",
    description: "Advanced care for endometriosis including diagnosis, pain management, and surgical interventions."
  },
  {
    icon: "UserGroupIcon",
    title: "Obstetric Care",
    description: "Complete pregnancy care from conception to delivery with expert monitoring and support throughout."
  },
  {
    icon: "ClipboardDocumentCheckIcon",
    title: "Family Planning",
    description: "Confidential family planning services including contraception counseling and reproductive health education."
  },
  {
    icon: "CalendarDaysIcon",
    title: "ANC Services",
    description: "Comprehensive antenatal care with regular check-ups, screenings, and preparation for a healthy delivery."
  },
  {
    icon: "AcademicCapIcon",
    title: "Laboratory Services",
    description: "On-site laboratory testing for accurate diagnosis and monitoring of gynecological and obstetric conditions."
  },
  {
    icon: "CameraIcon",
    title: "Ultrasonography",
    description: "Advanced ultrasound imaging for pregnancy monitoring, gynecological assessments, and diagnostic procedures."
  }
];

export default function ServicesGrid() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardsRef.current.indexOf(entry.target as HTMLDivElement);
            if (index !== -1 && !visibleCards.includes(index)) {
              setTimeout(() => {
                setVisibleCards((prev) => [...prev, index]);
              }, index * 100);
            }
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -80px 0px" }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, [visibleCards]);

  return (
    <section id="services" className="relative py-16 sm:py-20 md:py-24 px-4 sm:px-6 overflow-hidden gpu-accelerated" ref={sectionRef}>
      {/* Background with subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-diamond via-pink-pale to-silver-light opacity-60" />
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-10 w-96 h-96 bg-light-purple rounded-full mix-blend-multiply filter blur-3xl animate-float-b" />
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-pink-brand rounded-full mix-blend-multiply filter blur-3xl animate-float-c" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-14 md:mb-16 hero-animate">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-royal-purple mb-3 sm:mb-4 px-4">
            Our <span className="gradient-text-gold">Specialties</span>
          </h2>
          <p className="text-brand-muted text-base sm:text-lg max-w-2xl mx-auto px-4">
            Comprehensive gynecological and obstetric care tailored to your unique needs
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
              className={`group relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 sm:p-8 border border-amber-400/20 shadow-lg transition-all duration-500 overflow-hidden cursor-pointer gpu-accelerated min-h-[44px] touch-manipulation ${
                visibleCards.includes(index)
                  ? "opacity-100 translate-y-0 scale-100" :"opacity-0 translate-y-12 scale-95"
              } hover:shadow-2xl hover:-translate-y-2 md:hover:scale-105 hover:scale-[1.02] active:scale-100 active:shadow-xl hover:border-amber-500/40`}
              style={{
                transitionDelay: visibleCards.includes(index) ? "0s" : `${index * 0.1}s`,
                transform: "translateZ(0)",
                willChange: "transform, opacity"
              }}>
              {/* Gradient overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-navy-900/5 via-amber-500/10 to-amber-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Animated border glow with amber */}
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 rounded-2xl border-2 border-amber-500/60 animate-pulse" />
              </div>

              {/* Shimmer effect on hover */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-amber-400/20 to-transparent" />

              <div className="relative z-10">
                {/* Icon */}
                <div className="mb-6 sm:mb-6 inline-flex items-center justify-center w-16 h-16 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-navy-800 to-amber-600 shadow-lg transition-all duration-500 gpu-accelerated md:group-hover:scale-110 md:group-hover:rotate-6 group-hover:scale-105 group-hover:rotate-3 group-active:scale-95 group-hover:shadow-amber-500/50 min-h-[44px] min-w-[44px]">
                  <Icon name={service.icon} size={28} variant="solid" className="text-white transition-transform duration-500 md:group-hover:scale-110 group-hover:scale-105 w-7 h-7 sm:w-8 sm:h-8" />
                </div>

                {/* Title */}
                <h3 className="font-display text-xl sm:text-xl font-semibold text-navy-900 mb-3 sm:mb-3 group-hover:text-amber-600 transition-colors duration-500 color-transition">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-brand-muted text-sm sm:text-sm leading-relaxed group-hover:text-foreground transition-colors duration-500 color-transition">
                  {service.description}
                </p>

                {/* Hover indicator */}
                <div className="mt-6 sm:mt-6 flex items-center gap-2 text-amber-600 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-2 transition-all duration-500 min-h-[44px] items-center">
                  <span className="text-sm sm:text-sm font-medium">Learn more</span>
                  <Icon name="ArrowRightIcon" size={16} variant="solid" className="transition-transform duration-300 group-hover:translate-x-1" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12 sm:mt-16">
          <a
            href="#contact"
            className="btn-gold inline-flex items-center gap-3 sm:gap-3 px-8 sm:px-8 py-4 sm:py-4 text-base sm:text-base font-semibold shadow-xl hover:shadow-2xl transition-all duration-500 md:hover:scale-105 hover:scale-[1.02] active:scale-95 hover:bg-pink-brand hover:border-pink-brand gpu-accelerated min-h-[44px] touch-manipulation">
            <Icon name="CalendarDaysIcon" size={20} variant="solid" className="transition-transform duration-300 group-hover:rotate-12" />
            Schedule Your Consultation
          </a>
        </div>
      </div>
    </section>
  );
}